import Link from "next/link"
import {
  AlertTriangle,
  Calculator,
  MessageCircle,
  ShoppingBasket,
  Snowflake,
  Star,
} from "lucide-react"

import type { Recipe } from "@/types/recipe"
import { KuhlykTip } from "@/components/kuhlyk/kyhlyk-tip"
import { SaveRecipeButton } from "@/components/recipes/save-recipe-button"

type RecipeSidebarProps = {
  recipe: Recipe
  isSaved?: boolean
  isAuthenticated?: boolean
}

type SidebarLinkItem = {
  title: string
  href: string
}

function getHelpfulLinks(recipe: Recipe): SidebarLinkItem[] {
  if (recipe.category === "fermentation") {
    return [
      {
        title: "Калькулятор солі та цукру",
        href: "/calculators/salt-sugar",
      },
      {
        title: "Гайди з ферментації та безпеки",
        href: "/guides",
      },
      {
        title: "Підібрати рецепти за інгредієнтами",
        href: "/ingredient-finder",
      },
    ]
  }

  if (recipe.category === "jam" || recipe.category === "compote") {
    return [
      {
        title: "Конвертер оцту",
        href: "/calculators/vinegar-converter",
      },
      {
        title: "Гайди та поради",
        href: "/guides",
      },
      {
        title: "Підібрати рецепти за інгредієнтами",
        href: "/ingredient-finder",
      },
    ]
  }

  return [
    {
      title: "Калькулятор маринаду",
      href: "/calculators/marinade",
    },
    {
      title: "Конвертер оцту",
      href: "/calculators/vinegar-converter",
    },
    {
      title: "Гайди та поради",
      href: "/guides",
    },
  ]
}

function getQuickActions(recipe: Recipe): SidebarLinkItem[] {
  return [
    {
      title: "Перейти до відгуків",
      href: `/recipes/${recipe.slug}#reviews`,
    },
    {
      title: "Перейти до коментарів",
      href: `/recipes/${recipe.slug}#comments`,
    },
  ]
}

export function RecipeSidebar({
  recipe,
  isSaved = false,
  isAuthenticated = false,
}: RecipeSidebarProps) {
  const helpfulLinks = getHelpfulLinks(recipe)
  const quickActions = getQuickActions(recipe)

  return (
    <aside className="space-y-5">
      <SaveRecipeButton
        recipeId={recipe.id}
        initialSaved={isSaved}
        isAuthenticated={isAuthenticated}
        redirectPath={`/recipes/${recipe.slug}`}
      />

      {recipe.kuhlykTip ? <KuhlykTip text={recipe.kuhlykTip} /> : null}

      <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
          <Star className="h-4 w-4 text-emerald-700" />
          Швидкі дії
        </div>

        <div className="space-y-3">
          {quickActions.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block rounded-[1.25rem] border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-800 transition-colors hover:border-emerald-200 hover:bg-emerald-50"
            >
              {item.title}
            </Link>
          ))}
        </div>
      </div>

      {recipe.storageInfo ? (
        <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
            <Snowflake className="h-4 w-4 text-emerald-700" />
            Зберігання
          </div>

          <p className="text-sm leading-7 text-neutral-600">
            {recipe.storageInfo}
          </p>
        </div>
      ) : null}

      {recipe.safetyNotes && recipe.safetyNotes.length > 0 ? (
        <div className="rounded-[1.75rem] border border-rose-200 bg-rose-50/60 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
            <AlertTriangle className="h-4 w-4 text-rose-700" />
            Важливо для безпеки
          </div>

          <ul className="space-y-3 text-sm leading-7 text-neutral-700">
            {recipe.safetyNotes.map((note) => (
              <li key={note} className="flex gap-3">
                <span className="mt-1 text-rose-700">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
          <Calculator className="h-4 w-4 text-emerald-700" />
          Корисно поруч
        </div>

        <div className="space-y-3">
          {helpfulLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="block rounded-[1.25rem] border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-800 transition-colors hover:border-emerald-200 hover:bg-emerald-50"
            >
              {item.title}
            </Link>
          ))}
        </div>
      </div>

      <div className="rounded-[1.75rem] border border-neutral-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
          <ShoppingBasket className="h-4 w-4 text-emerald-700" />
          Що може знадобитися
        </div>

        {recipe.products && recipe.products.length > 0 ? (
          <div className="space-y-3">
            {recipe.products.map((product) => (
              <Link
                key={`${recipe.slug}-${product.title}`}
                href={product.href}
                className="block rounded-[1.25rem] border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-800 transition-colors hover:border-emerald-200 hover:bg-emerald-50"
              >
                {product.title}
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm leading-7 text-neutral-600">
            Для цього рецепта окремі товари не зазначені, але в розділі з
            товарами можна знайти базовий інвентар для домашніх заготовок.
          </p>
        )}
      </div>

      <div className="rounded-[1.75rem] border border-neutral-200 bg-gradient-to-br from-white to-emerald-50/50 p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-950">
          <MessageCircle className="h-4 w-4 text-emerald-700" />
          Навіщо це на сторінці
        </div>

        <p className="text-sm leading-7 text-neutral-600">
          Сайдбар допомагає не лише читати рецепт, а й швидко перейти до
          розрахунків, пояснень, обговорення та пов’язаних практичних дій без
          зайвого пошуку по сайту.
        </p>
      </div>
    </aside>
  )
}